/**
 * Region defaults for the languages we ship. Region decides formatting conventions such as the
 * hour cycle, never wording: `fr-CA` gives "13 h 30", `es-419` gives "1:30 p. m.", `en-US` gives
 * "1:30 PM".
 */
const DEFAULT_REGIONS: Record<string, string> = {
	en: "US",
	fr: "CA",
	es: "419",
};

type LocaleParts = {
	language?: string;
	region?: string;
};

const splitLocale = (locale: string | undefined): LocaleParts => {
	const [language, region] = (locale ?? "").split("-");
	return {
		language: language ? language.toLowerCase() : undefined,
		region: region ? region.toUpperCase() : undefined,
	};
};

type ResolveDisplayLocaleProps = {
	language?: string | undefined;
	companyLocale?: string | undefined;
	fallbackLocale: string;
};

/**
 * Dates and times render in the language the user reads the UI in, with that language's own region
 * conventions. The company's region deliberately does NOT override them: an English user at a
 * Canadian company still reads "12 AM", not "12 a.m.". Company locale only supplies the language
 * when the UI has not told us one. The result is deterministic so a server-rendered time matches
 * what the browser renders after hydration.
 */
export const resolveDisplayLocale = ({
	language,
	companyLocale,
	fallbackLocale,
}: ResolveDisplayLocaleProps): string => {
	const company = splitLocale(companyLocale);
	const requested = splitLocale(language);
	const resolvedLanguage = requested.language ?? company.language;

	if (!resolvedLanguage) {
		return fallbackLocale;
	}

	// The language alone decides the region, so the variant we ship is enforced rather than
	// negotiated — a stray `fr-FR` or a Canadian company cannot change how anyone's times render.
	const resolvedRegion = DEFAULT_REGIONS[resolvedLanguage];
	return resolvedRegion ? `${resolvedLanguage}-${resolvedRegion}` : resolvedLanguage;
};
